import { useRef, useEffect } from 'react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Employee } from '../../../hooks/useEmployeeData';
import { colors, shadows } from '../styles/personalakteStyles';

interface EmployeeActionMenuProps {
  employee: Employee | undefined;
  actionMenuPos: { top: number; left: number };
  onClose: () => void;
  onEdit: (emp: Employee) => void;
  onChangePassword: (emp: Employee) => void;
  onResendInvite: (emp: Employee) => void;
  onActivate: (employeeId: number) => void;
  onReactivate: (employeeId: number) => void;
  onDeactivate: (employeeId: number) => void;
  onFinalize: (emp: Employee) => void;
  activatingUser: number | null;
  resendInviteEmployeeId: number | null;
}

const actionItemStyle: React.CSSProperties = {
  padding: '8px 12px',
  cursor: 'pointer',
  borderRadius: '10px',
  fontSize: '12px',
  color: colors.textBody,
  transition: 'background 0.15s',
};

function EmployeeActionMenu({
  employee,
  actionMenuPos,
  onClose,
  onEdit,
  onChangePassword,
  onResendInvite,
  onActivate,
  onReactivate,
  onDeactivate,
  onFinalize,
  activatingUser,
  resendInviteEmployeeId,
}: EmployeeActionMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!employee) return null;

  const isResendLoading = resendInviteEmployeeId === employee.id;
  const isUserActivating = activatingUser === employee.id;
  const isUserDisabled = employee.onboarding_status === 'DEACTIVATED';

  return (
    <div ref={menuRef} style={{
      position: 'fixed',
      top: actionMenuPos.top,
      right: window.innerWidth - actionMenuPos.left,
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      padding: '8px',
      minWidth: '120px',
      boxShadow: shadows.elevated,
      zIndex: 10000,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '4px',
        paddingBottom: '4px',
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        <span style={{ fontSize: '0.8rem', color: colors.textMuted, fontWeight: 'bold' }}>Actions</span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            cursor: 'pointer',
            color: colors.textMuted,
            padding: '0',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {isUserDisabled ? (
        <div
          style={{ ...actionItemStyle, opacity: isUserActivating ? 0.6 : 1 }}
          onClick={() => !isUserActivating && onReactivate(employee.id)}
        >
          {isUserActivating ? 'Reactivating...' : 'Reactivate'}
        </div>
      ) : (
        <>
          <div
            style={actionItemStyle}
            onClick={() => {
              onClose();
              navigate(`/employees/${employee.id}`);
            }}
            data-testid={`employee-edit-${employee.id}`}
          >
            Edit
          </div>
          {employee.user && (
            <div
              style={actionItemStyle}
              onClick={() => onChangePassword(employee)}
            >
              Change PW
            </div>
          )}
          {employee.onboarding_status === 'INVITED' && (
            <div
              style={actionItemStyle}
              onClick={() => onResendInvite(employee)}
            >
              {isResendLoading ? 'Resending...' : 'Resend Invite'}
            </div>
          )}
          {!employee.user && (
            <div
              style={{ ...actionItemStyle, opacity: isUserActivating ? 0.6 : 1 }}
              onClick={() => !isUserActivating && onActivate(employee.id)}
            >
              {isUserActivating ? 'Activating...' : 'Activate'}
            </div>
          )}
          {employee.onboarding_status === 'WAITING_FOR_MANAGER' && (
            <div
              style={actionItemStyle}
              onClick={() => onFinalize(employee)}
            >
              Check/Finalize
            </div>
          )}
          <div
            style={actionItemStyle}
            onClick={() => onDeactivate(employee.id)}
          >
            Deactivate
          </div>
        </>
      )}
    </div>
  );
}

export default EmployeeActionMenu;
